import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Combobox } from '@oysterjs/ui/combobox';
import React from 'react';
import { CaraWorkspaceArtifactType, HtmlDocumentTemplateFieldsFragment } from '~/api/graphql';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { BUILTIN_STYLES } from '~/components/workspaces/lib/artifact';
import { useDocumentDesigns } from '~/components/workspaces/hooks/use-document-designs';
import {
  exportPages,
  UNRESOLVED_DESIGN,
  type ExportDesign
} from '~/components/workspaces/lib/export-pages';
import {
  filterHtmlDocumentStyleRecords,
  getArtifactHtmlDocumentTemplateId,
  htmlDocumentTemplateHasBody,
  resolveHtmlDocumentPdfExportStyleId,
  type HtmlDocumentArtifactRef
} from '~/components/workspaces/lib/html-document-artifact';
import { ExportPagesSummary } from './export-pages-summary';
import { WorkspacePdfFile } from './artifact-focus-html-document-render-pdf-attachment';
import { OpenPdfWithAttachmentsButton } from './artifact-open-pdf-with-attachments-button';
import { usePdfAttachmentExport } from '~/api/data/workspaces/use-pdf-attachment-export';
import {
  buildWorkspaceAttachmentCatalog,
  latestHTMLArtifacts,
  type WorkspaceAttachableArtifact
} from '~/components/workspaces/lib/pdf-attachment-export';

export { MENU_PANEL_CLASS } from './artifact-open-pdf-with-attachments-button';

type BaseArtifact = HtmlDocumentArtifactRef & {
  type?: CaraWorkspaceArtifactType;
  document?: { resolvedDesign?: { designId?: string | null } | null; final?: unknown } | null;
};

type RenderArtifactButtonProps<T extends BaseArtifact> = {
  workspaceId: string;
  artifact: T;
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
  workspaceArtifacts?: WorkspaceAttachableArtifact[];
  workspaceFiles?: WorkspacePdfFile[];
  workspaceMessageFiles?: WorkspacePdfFile[];
  /** Style chosen in the Document Style popover; shared with editor preview. */
  selectedStyleId: string | null;
  onSelectedStyleIdChange: (id: string | null) => void;
  refitDesign?: boolean;
  /** Held while the document is updating to its Design, which would replace the version. */
  disabled?: boolean;
};

export { getArtifactHtmlDocumentTemplateId } from '~/components/workspaces/lib/html-document-artifact';

export function buildSessionCatalogMap(
  workspaceFiles: WorkspacePdfFile[] | undefined,
  workspaceMessageFiles: WorkspacePdfFile[] | undefined,
  effectiveTemplate: HtmlDocumentTemplateFieldsFragment | undefined,
  workspaceArtifacts?: WorkspaceAttachableArtifact[],
  currentArtifactId?: string,
  selectedArtifactIds: string[] = [],
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[] = []
): Map<string, WorkspacePdfFile> {
  const m = buildWorkspaceAttachmentCatalog(
    workspaceFiles,
    workspaceMessageFiles,
    workspaceArtifacts,
    currentArtifactId
  );
  for (const f of effectiveTemplate?.prefixFiles ?? []) {
    if (!m.has(f.id)) {
      m.set(f.id, {
        kind: 'file',
        id: f.id,
        name: f.name,
        mimeType: f.mimeType ?? 'application/pdf'
      });
    }
  }
  for (const f of effectiveTemplate?.suffixFiles ?? []) {
    if (!m.has(f.id)) {
      m.set(f.id, {
        kind: 'file',
        id: f.id,
        name: f.name,
        mimeType: f.mimeType ?? 'application/pdf'
      });
    }
  }
  addSelectedArtifactTemplateFiles(
    m,
    selectedArtifactIds,
    workspaceArtifacts,
    htmlDocumentTemplates
  );
  return m;
}

function addFileToCatalog(
  m: Map<string, WorkspacePdfFile>,
  f: { id: string; name: string; mimeType?: string | null }
) {
  if (!m.has(f.id)) {
    m.set(f.id, {
      kind: 'file',
      id: f.id,
      name: f.name,
      mimeType: f.mimeType ?? 'application/pdf'
    });
  }
}

export function addSelectedArtifactTemplateFiles(
  m: Map<string, WorkspacePdfFile>,
  selectedArtifactIds: string[],
  workspaceArtifacts?: WorkspaceAttachableArtifact[],
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[] = []
) {
  const selected = new Set(selectedArtifactIds);
  for (const artifact of latestHTMLArtifacts(workspaceArtifacts ?? [])) {
    if (!selected.has(artifact.id)) continue;
    const templateId = getArtifactHtmlDocumentTemplateId(artifact);
    if (!templateId) continue;
    const template = htmlDocumentTemplates.find((t) => t.id === templateId);
    for (const f of template?.prefixFiles ?? []) addFileToCatalog(m, f);
    for (const f of template?.suffixFiles ?? []) addFileToCatalog(m, f);
  }
}

function useTemplateAttachmentDefaults(
  artifact: BaseArtifact,
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[],
  selectedStyleId: string | null
) {
  return React.useMemo(() => {
    const artifactTemplateId = getArtifactHtmlDocumentTemplateId(artifact);
    const effectiveTemplateId =
      artifactTemplateId ??
      (selectedStyleId && !selectedStyleId.startsWith('builtin:') ? selectedStyleId : null);
    const effectiveTemplate = effectiveTemplateId
      ? htmlDocumentTemplates.find((x) => x.id === effectiveTemplateId)
      : undefined;
    return {
      defaultPrefix: effectiveTemplate?.prefixFiles?.map((f) => f.id) ?? [],
      defaultSuffix: effectiveTemplate?.suffixFiles?.map((f) => f.id) ?? [],
      effectiveTemplate
    };
  }, [
    artifact.id,
    artifact.version,
    artifact.document?.htmlDocumentTemplateId,
    htmlDocumentTemplates,
    selectedStyleId
  ]);
}

/**
 * The design a version renders with, from the designs the agency lists or else the one its
 * template carries, which also covers an archived design the list leaves out. A design named
 * but found in neither is unresolved, not absent.
 */
function resolveExportDesign(
  designId: string | null,
  choices: ReturnType<typeof useDocumentDesigns>['choices'],
  template: HtmlDocumentTemplateFieldsFragment | undefined
): ExportDesign | typeof UNRESOLVED_DESIGN | null {
  if (!designId) return null;
  const listed = choices?.designs.find((each) => each.id === designId);
  if (listed) return listed;
  return template?.design?.id === designId ? template.design : UNRESOLVED_DESIGN;
}

export const RenderArtifactButton = <T extends BaseArtifact>({
  workspaceId,
  artifact,
  htmlDocumentTemplates,
  workspaceArtifacts,
  workspaceFiles,
  workspaceMessageFiles,
  selectedStyleId,
  onSelectedStyleIdChange,
  refitDesign = false,
  disabled = false
}: RenderArtifactButtonProps<T>) => {
  const linkedTemplateId = getArtifactHtmlDocumentTemplateId(artifact);
  const isTemplateBacked = linkedTemplateId != null;
  const designsOn = useFlag(FEATURE_FLAGS.AGENCY_BRAND_DESIGN);
  const final = designsOn && !!artifact.document?.final;
  const templateDefaults = useTemplateAttachmentDefaults(
    artifact,
    htmlDocumentTemplates,
    selectedStyleId
  );
  const { effectiveTemplate } = templateDefaults;

  const { choices: designChoices } = useDocumentDesigns();
  const linkedTemplate = linkedTemplateId
    ? htmlDocumentTemplates.find((each) => each.id === linkedTemplateId)
    : undefined;
  const designId = artifact.document?.resolvedDesign?.designId ?? null;
  const exportDesign = resolveExportDesign(designId, designChoices, linkedTemplate);
  // A Style has no start or end choice, and its files stay offered as attachments. Any other
  // template-backed document prints its pages as the server decides, from the template's choice
  // for each end, so the template's files are not also offered as attachments.
  const isStyleLinked =
    linkedTemplateId != null &&
    (linkedTemplateId.startsWith('builtin:') ||
      (!!linkedTemplate && !htmlDocumentTemplateHasBody(linkedTemplate)));
  const serverChoosesPages = final || (designsOn && isTemplateBacked && !isStyleLinked);
  // A template this caller cannot read has no start or end choice to show. A document without a
  // template waits for its design to load, since an end it supplies would look empty.
  const summarizable = linkedTemplateId
    ? !!linkedTemplate && htmlDocumentTemplateHasBody(linkedTemplate)
    : !designId || (exportDesign !== null && exportDesign !== UNRESOLVED_DESIGN);
  const pages =
    designsOn && !final && summarizable ? exportPages(exportDesign, linkedTemplate ?? null) : null;
  const defaultPrefix = serverChoosesPages ? [] : templateDefaults.defaultPrefix;
  const defaultSuffix = serverChoosesPages ? [] : templateDefaults.defaultSuffix;

  const attachmentResetKey = `${artifact.id}:${artifact.version}:${linkedTemplateId ?? ''}:${selectedStyleId ?? ''}:${serverChoosesPages}:${final ? 'final' : 'draft'}`;

  const styleIdForUrl = resolveHtmlDocumentPdfExportStyleId(linkedTemplateId, selectedStyleId);

  const buildInitialCatalog = React.useCallback(
    () =>
      buildSessionCatalogMap(
        workspaceFiles,
        workspaceMessageFiles,
        final ? undefined : effectiveTemplate,
        workspaceArtifacts,
        artifact.id,
        [],
        htmlDocumentTemplates
      ),
    [
      workspaceFiles,
      workspaceMessageFiles,
      final,
      effectiveTemplate,
      workspaceArtifacts,
      artifact.id,
      htmlDocumentTemplates
    ]
  );

  const {
    prefixFileIds,
    suffixFileIds,
    setLocalFiles,
    attachmentCatalogList,
    onAttachmentChange,
    isOpening,
    openError,
    openNotice,
    handleOpenPdf
  } = usePdfAttachmentExport({
    workspaceId,
    artifactId: artifact.id,
    version: artifact.version,
    refitDesign: designsOn && !final && refitDesign,
    workspaceFiles,
    workspaceMessageFiles,
    workspaceArtifacts,
    defaultPrefixFileIds: defaultPrefix,
    defaultSuffixFileIds: defaultSuffix,
    resetKey: attachmentResetKey,
    buildInitialCatalog,
    buildExtraInput: () => ({
      styleId: styleIdForUrl,
      // The server applies the template's choice for each end, and the pages frozen with a
      // final version, so the client names neither the template's files nor the design's.
      skipTemplateFiles: !serverChoosesPages,
      // Skipping the template's files leaves out the design's pages too, which a document
      // without a template prints as part of its look.
      includeDesignPages: designsOn && !final && !isTemplateBacked
    })
  });

  // Extend catalog with template files when user selects HTML artifact attachments.
  const extendedCatalog = React.useMemo(() => {
    const m = new Map(attachmentCatalogList.map((f) => [f.id, f]));
    addSelectedArtifactTemplateFiles(
      m,
      [...prefixFileIds, ...suffixFileIds],
      workspaceArtifacts,
      htmlDocumentTemplates
    );
    return [...m.values()];
  }, [
    attachmentCatalogList,
    prefixFileIds,
    suffixFileIds,
    workspaceArtifacts,
    htmlDocumentTemplates
  ]);

  return (
    <OpenPdfWithAttachmentsButton
      onOpenPdf={handleOpenPdf}
      isOpening={isOpening}
      openError={openError}
      openNotice={openNotice}
      disabled={disabled}
      attachmentCatalog={extendedCatalog}
      prefixFileIds={prefixFileIds}
      suffixFileIds={suffixFileIds}
      onAttachmentChange={onAttachmentChange}
      onLocalFilesChange={setLocalFiles}
    >
      {final ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Start and end pages are frozen with this final version. Later changes to its template or
          design do not change those pages.
        </p>
      ) : pages ? (
        <ExportPagesSummary start={pages.start} end={pages.end} />
      ) : null}
      {/* With designs on, the document's design decides its look, set from the Design menu. */}
      {!designsOn && (
        <div>
          <h3 className="text-sm font-medium font-body mt-0">Document Style</h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Pick a built-in or custom style for this export.
          </p>
          <SelectStyleSection
            isTemplateBacked={isTemplateBacked}
            selectedStyleId={selectedStyleId}
            onSelectStyle={onSelectedStyleIdChange}
            htmlDocumentTemplates={htmlDocumentTemplates}
          />
        </div>
      )}
    </OpenPdfWithAttachmentsButton>
  );
};

const UNSTYLED_VALUE = '__unstyled__';

function SelectStyleSection(props: {
  isTemplateBacked: boolean;
  selectedStyleId: string | null;
  onSelectStyle: (id: string | null) => void;
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
}) {
  const { isTemplateBacked, selectedStyleId, onSelectStyle, htmlDocumentTemplates } = props;

  const options = React.useMemo(() => {
    const customStyles = filterHtmlDocumentStyleRecords(htmlDocumentTemplates);
    const builtInOptions = BUILTIN_STYLES.map((s) => ({
      value: s.styleId,
      label: s.label,
      group: 'Built-in styles' as const
    }));
    const customOptions = customStyles.map((t) => ({
      value: t.id,
      label: t.title,
      group: 'Custom styles' as const
    }));
    const styleOptions =
      customOptions.length > 0 ? [...customOptions, ...builtInOptions] : builtInOptions;
    return [{ value: UNSTYLED_VALUE, label: 'Unstyled' }, ...styleOptions];
  }, [htmlDocumentTemplates]);

  return (
    <div className="mt-2 space-y-2">
      <Combobox
        value={selectedStyleId ?? UNSTYLED_VALUE}
        placeholder="Search styles…"
        minQueryLength={0}
        maxOptions={50}
        options={options}
        disabled={isTemplateBacked}
        icon={isTemplateBacked ? <LockClosedIcon className="size-4" /> : undefined}
        onChange={(v) => onSelectStyle(v === UNSTYLED_VALUE || v === '' ? null : v)}
      />
      {isTemplateBacked && (
        <p className="rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
          Style is determined by the selected template and cannot be changed independently.
        </p>
      )}
    </div>
  );
}
